import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, usePermissions } from '../../hooks';
import ReactMarkdown from 'react-markdown';
import {
  getClientProposal,
  getProposalBranding,
  verifyProposalEmail,
  addClientComment,
  deleteClientComment,
  approveProposal,
  rejectProposal,
} from '../../api/publicProposals';
import ProposalChatPanel from '../../components/proposal/ProposalChatPanel';
import LiveKitAvatarPanel from '../../components/proposal/LiveKitAvatarPanel';
import useLiveKitAvatarV4 from '../../hooks/useLiveKitAvatarV4';
import InactiveProposalBanner from '../../components/proposal/InactiveProposalBanner';
import { buildTheme, ProposalThemeContext, useProposalTheme } from '../../utils/proposalTheme';

/* ─────────────────────────────────────────────────────
   Client Proposal Review Page V4 — Presenter + Avatar
   Light professional theme: white bg, navy headings,
   sage-green accents, Libre Caslon Text + Geist.
   ───────────────────────────────────────────────────── */

/* Brand tokens are delivered at runtime via ProposalThemeContext (see
   utils/proposalTheme.js). Each helper below reads useProposalTheme(); the main
   component builds the theme from data.org_branding and provides it. */

/* ── Helpers ── */

const getSectionPill = (title) => {
  if (!title) return 'Section';
  const lower = title.toLowerCase();
  if (lower.includes('overview') || lower.includes('summary')) return 'Overview';
  if (lower.includes('market')) return 'Market';
  if (lower.includes('financial') || lower.includes('finance')) return 'Financials';
  if (lower.includes('team')) return 'Team';
  if (lower.includes('risk')) return 'Risk';
  if (lower.includes('structure')) return 'Structure';
  if (lower.includes('term')) return 'Terms';
  if (lower.includes('timeline') || lower.includes('milestones')) return 'Timeline';
  if (lower.includes('recommendation')) return 'Recommendation';
  if (lower.includes('appendix') || lower.includes('appendices')) return 'Appendix';
  return title.split(' ').slice(0, 2).join(' ');
};

/* Splits a heading so the last word is gold-accented */
const AccentHeading = ({ text, className = '', style = {} }) => {
  const Z = useProposalTheme();
  if (!text) return null;
  const words = text.split(' ');
  if (words.length <= 1) return <span className={className} style={style}>{text}</span>;
  const lead = words.slice(0, -1).join(' ') + ' ';
  const last = words[words.length - 1];
  return (
    <span className={className} style={style}>
      {lead}<span style={{ color: Z.gold, fontStyle: 'italic' }}>{last}</span>
    </span>
  );
};

/* Markdown renderer using react-markdown — factory so it captures the live theme */
const makeMdComponents = (Z) => ({
  p: ({ children }) => {
    return <span className="block mb-1 last:mb-0">{children}</span>;
  },
  strong: ({ children }) => <strong style={{ color: Z.heading, fontWeight: 600 }}>{children}</strong>,
  em: ({ children }) => <em style={{ color: Z.goldLight }}>{children}</em>,
  ul: ({ children }) => <ul className="mt-1">{children}</ul>,
  ol: ({ children }) => <ol className="mt-1 list-decimal list-inside">{children}</ol>,
  li: ({ children }) => (
    <li className="flex items-start gap-1.5 mb-0.5">
      <span className="w-1 h-1 rounded-full mt-[7px] shrink-0" style={{ background: Z.gold }} />
      <span className="[&>span]:mb-0 [&>span]:inline">{children}</span>
    </li>
  ),
  h3: ({ children }) => <h3 className="text-lg font-semibold mb-2" style={{ color: Z.heading }}>{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold mb-1" style={{ color: Z.heading }}>{children}</h4>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: Z.gold }}>{children}</a>,
});
const Md = ({ children }) => {
  const Z = useProposalTheme();
  const components = useMemo(() => makeMdComponents(Z), [Z]);
  if (!children || typeof children !== 'string') return children || null;
  return <ReactMarkdown components={components}>{children}</ReactMarkdown>;
};

const entrance = (active, delay = 0, direction = 'up') => {
  const t = {
    up: 'translateY(40px)',
    down: 'translateY(-30px)',
    left: 'translateX(-50px)',
    right: 'translateX(50px)',
    scale: 'scale(0.93)',
  };
  return {
    opacity: active ? 1 : 0,
    transform: active ? 'none' : t[direction],
    transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
  };
};

const isPlaceholder = (val) => val?.toLowerCase().trim() === 'subject to further analysis';

/* Returns true for numeric values, percentages, or placeholder text */
const isMetricNumeric = (val) => {
  if (!val) return false;
  if (isPlaceholder(val)) return true;
  return /[\d%×x–\-~+]/.test(val);
};

/* ── Subsection layout detection ── */
const detectSubsectionLayout = (subsections) => {
  if (!subsections || subsections.length === 0) return 'cards';
  const hasBullets = subsections.some((s) => s.bullet_points && s.bullet_points.length > 0);
  if (hasBullets) return 'cards';
  const contents = subsections.filter((s) => s.content).map((s) => s.content.length);
  const avgLen = contents.length > 0 ? contents.reduce((a, b) => a + b, 0) / contents.length : 0;
  if (subsections.length >= 6 && avgLen < 80) return 'table';
  return 'role';
};

/* ── Visual sub-components ── */

const Orb = React.memo(({ size = 100, logoUrl, hideWhenEmpty = false }) => (
  <div className="relative" style={{ width: size, height: size }}>
    {logoUrl ? (
      <img src={logoUrl} alt="" className="w-full h-full object-contain" />
    ) : hideWhenEmpty ? null : (
      <img src="/images/logo-icon.svg" alt="" className="w-full h-full object-contain" />
    )}
  </div>
));
Orb.displayName = 'Orb';

const SlideBg = React.memo(() => {
  const Z = useProposalTheme();
  return <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" style={{ background: Z.bg }} />;
});
SlideBg.displayName = 'SlideBg';

const Card = ({ children, className = '', style = {}, bg, borderColor }) => {
  const Z = useProposalTheme();
  return (
    <div className={`relative ${className}`} style={style}>
      <div className="relative rounded-[26px] overflow-hidden"
        style={{ background: bg || Z.bgCard, border: `1px solid ${borderColor || Z.borderSubtle}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' }}>
        {children}
      </div>
    </div>
  );
};

/* ── Hybrid subsection renderer ── */
const SubsectionRenderer = ({ subsections, isActive, baseDelay = 0.3, className = '' }) => {
  const Z = useProposalTheme();
  const layout = useMemo(() => detectSubsectionLayout(subsections), [subsections]);
  if (!subsections || subsections.length === 0) return null;

  /* ── TABLE: open layout, gold values, no box ── */
  if (layout === 'table') {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-0 ${className}`}>
        {subsections.map((sub, si) => (
          <div key={si}
            className="flex items-baseline justify-between py-2 md:py-3 gap-3 md:gap-4"
            style={{
              ...entrance(isActive, baseDelay + 0.05 + si * 0.03, 'up'),
              borderBottom: `1px solid ${Z.borderSubtle}`,
            }}>
            <span className="text-[10px] md:text-xs font-medium uppercase tracking-wider shrink-0" style={{ color: Z.gray400 }}>
              {sub.subtitle}
            </span>
            <span className="text-xs md:text-sm font-semibold text-right" style={{ color: Z.gold }}>
              {sub.content}
            </span>
          </div>
        ))}
      </div>
    );
  }

  /* ── ROLE: clean left-border with gold badge ── */
  if (layout === 'role') {
    return (
      <div className={`grid gap-x-4 gap-y-6 md:gap-y-10 ${subsections.length <= 2 ? 'grid-cols-1 md:grid-cols-2' : subsections.length === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${className}`}>
        {subsections.map((sub, si) => (
          <div key={si}
            className="pl-4 md:pl-5"
            style={{
              ...entrance(isActive, baseDelay + si * 0.08, si % 2 === 0 ? 'left' : 'right'),
              borderLeft: `3px solid ${Z.gold}`,
            }}>
            {sub.subtitle && (
              <span className="inline-block text-[10px] md:text-xs font-semibold uppercase tracking-wider px-2 py-0.5 md:px-2.5 md:py-1 rounded-md mb-2 md:mb-3"
                style={{ color: Z.gold, background: 'rgba(91,140,82,0.1)', border: `1px solid ${Z.border}` }}>
                {sub.subtitle}
              </span>
            )}
            {sub.content && (
              <div className="text-sm md:text-base leading-relaxed" style={{ color: Z.gray400 }}>
                <Md>{sub.content}</Md>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  /* ── CARDS: default — subtitle + bullets, clean left-border style ── */
  return (
    <div className={`grid gap-4 md:gap-6 ${subsections.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : subsections.length === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : subsections.length === 2 ? 'md:grid-cols-2' : 'grid-cols-1'} ${className}`}>
      {subsections.map((sub, si) => (
        <div key={si}
          className="pl-4 md:pl-5"
          style={{
            ...entrance(isActive, baseDelay + si * 0.06, 'up'),
            borderLeft: `3px solid ${Z.gold}`,
          }}>
          {sub.subtitle && (
            <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-2 md:mb-3"
              style={{ color: Z.heading }}>
              {sub.subtitle}
            </p>
          )}
          {sub.content && (
            <div className="text-sm md:text-base leading-relaxed" style={{ color: Z.gray400 }}><Md>{sub.content}</Md></div>
          )}
          {sub.bullet_points && (
            <ul className="mt-2 space-y-1.5">
              {sub.bullet_points.map((bp, bi) => (
                <li key={bi} className="text-sm md:text-base flex items-start gap-2" style={{ color: Z.gray400 }}>
                  <span className="mt-[3px] text-base leading-none shrink-0" style={{ color: Z.gold }}>•</span>
                  <span>{bp}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};


/* Gold dashed line */
const DashedLine = ({ vertical = false, className = '' }) => {
  const Z = useProposalTheme();
  return (
    <div className={className}
      style={{
        [vertical ? 'width' : 'height']: '1px',
        background: vertical
          ? `repeating-linear-gradient(to bottom, ${Z.gold} 0px, ${Z.gold} 6px, transparent 6px, transparent 12px)`
          : `repeating-linear-gradient(to right, ${Z.gold} 0px, ${Z.gold} 6px, transparent 6px, transparent 12px)`,
      }}
    />
  );
};

/* ════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════ */

const ClientProposalReviewPageV4 = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [branding, setBranding] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [proposalOrgId, setProposalOrgId] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user, loading: authLoading } = useAuth();
  const { isSuperAdmin } = usePermissions();

  const [emailVerifiedState, setEmailVerifiedState] = useState(
    () => !!localStorage.getItem(`proposal_verified_${token}`)
  );
  // Org-scoped email-gate bypass: a logged-in portal user skips the email gate
  // ONLY when the proposal's owning org matches their access — super_admin always,
  // otherwise the proposal's org must be one of the user's memberships. A logged-in
  // user from a different org is treated as an external client (email gate).
  const orgMatches =
    isSuperAdmin ||
    (!!proposalOrgId && (user?.organizations || []).some((o) => o.id === proposalOrgId));

  const emailVerified = (!authLoading && !!user && orgMatches) || emailVerifiedState;
  // A logged-in portal user (member of the owning org) is previewing the proposal
  // (they skip the email gate). Client-facing actions (Comment, Accept, Send Back)
  // are only shown to external clients who accessed via the email gate — a cross-org
  // logged-in user is treated as an external client here too.
  const isPortalUser = !authLoading && !!user && orgMatches;
  const [emailInput, setEmailInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef(null);

  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [commentDrawerSection, setCommentDrawerSection] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [addingComment, setAddingComment] = useState(null);
  const [deletingComment, setDeletingComment] = useState(null);

  const [showDecisionDialog, setShowDecisionDialog] = useState(null);
  const [decisionComment, setDecisionComment] = useState('');
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const [chatOpen, setChatOpen] = useState(false);
  const [presenterActive, setPresenterActive] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const livekit = useLiveKitAvatarV4();

  // Runtime theme built from per-org branding (defaults until data loads).
  // Before email verification, `data` is null, so fall back to the branding-only
  // payload fetched on mount — this themes the Access Proposal gate too.
  const orgBranding = data?.org_branding || branding;
  const Z = useMemo(() => buildTheme(orgBranding), [orgBranding]);

  // Close panel when session ends
  useEffect(() => {
    if (livekit.agentState === 'ended' && presenterActive) {
      setPresenterActive(false);
    }
  }, [livekit.agentState, presenterActive]);

  /* ── Email ── */

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setVerifying(true);
    setEmailError('');
    try {
      const res = await verifyProposalEmail(token, { email: emailInput.trim() });
      if (res.data?.verified) {
        localStorage.setItem(`proposal_verified_${token}`, emailInput.trim());
        setEmailVerifiedState(true);
      } else {
        setEmailError('Email does not match our records');
      }
    } catch (err) {
      setEmailError(err?.response?.data?.detail || 'Unable to verify email');
    } finally {
      setVerifying(false);
    }
  };

  /* ── Data ── */

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await getClientProposal(token);
      setData(res.data);
    } catch (err) {
      if (!silent) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load proposal');
      } else {
        console.error('Silent fetchData background refresh failed:', err);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (emailVerified) fetchData();
  }, [emailVerified, fetchData]);

  // Fetch per-org branding up front (no email gate) so the Access Proposal
  // screen renders in the organization's theme before verification.
  useEffect(() => {
    let cancelled = false;
    getProposalBranding(token)
      .then((res) => {
        if (cancelled) return;
        setBranding(res.data?.org_branding || null);
        setOrgName(res.data?.organization_name || null);
        setProposalOrgId(res.data?.organization_id || null);
      })
      .catch(() => { /* fall back to V4 defaults; org stays null → fail-closed gate */ })
      .finally(() => { if (!cancelled) setBrandingLoaded(true); });
    return () => { cancelled = true; };
  }, [token]);

  // Brand the browser tab (title + favicon) for this client-facing page.
  // Uses the org logo as favicon and the org name in the title; restores the
  // app defaults on unmount.
  useEffect(() => {
    const faviconUrl = orgBranding?.favicon_url || orgBranding?.secondary_logo_url || orgBranding?.primary_logo_url;
    const link = document.querySelector("link[rel~='icon']");
    const prevTitle = document.title;
    const prevHref = link ? link.getAttribute('href') : null;

    document.title = orgName ? `${orgName} — Proposal` : 'Proposal Review';
    if (link && faviconUrl) link.setAttribute('href', faviconUrl);

    return () => {
      document.title = prevTitle;
      if (link && prevHref !== null) link.setAttribute('href', prevHref);
    };
  }, [orgName, orgBranding]);

  /* ── Navigation ── */

  const goToSlide = useCallback((index, totalSlides, instant = false, fromAgent = false) => {
    // Block manual slide navigation while avatar panel is open (allow agent-triggered advances)
    if (!fromAgent && presenterActive) return;
    const clamped = Math.max(0, Math.min(index, totalSlides - 1));
    const container = containerRef.current;
    if (container) container.scrollTo({ left: clamped * container.clientWidth, behavior: instant ? 'instant' : 'smooth' });
  }, [presenterActive]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Reset to first slide on mount (prevent browser scroll restoration)
    container.scrollTo({ left: 0, behavior: 'instant' });
    setCurrentSlide(0);
    const h = () => {
      const w = container.clientWidth;
      if (w > 0) setCurrentSlide(Math.round(container.scrollLeft / w));
    };
    container.addEventListener('scroll', h, { passive: true });
    return () => container.removeEventListener('scroll', h);
  }, [data]);


  /* ── Comments ── */

  const openComments = (n) => { setCommentDrawerSection(n); setCommentDrawerOpen(true); };

  const handleAddComment = async (n) => {
    const text = commentInputs[n]?.trim();
    if (!text) return;
    setAddingComment(n);
    try {
      await addClientComment(token, { section_number: n, comment: text });
      setCommentInputs((p) => ({ ...p, [n]: '' }));
      await fetchData(true);
    } catch { /* ignore */ }
    setAddingComment(null);
  };

  const handleDeleteComment = async (id) => {
    setDeletingComment(id);
    try { await deleteClientComment(token, id); await fetchData(true); } catch { /* */ }
    setDeletingComment(null);
  };

  /* ── Decision ── */

  const handleDecision = async (type) => {
    if (decisionComment.trim().length < 20) return;
    setSubmittingDecision(true);
    try {
      await (type === 'approve' ? approveProposal : rejectProposal)(token, { comment: decisionComment.trim() });
      setShowDecisionDialog(null);
      setDecisionComment('');
      setSuccessMessage(
        type === 'approve'
          ? 'Your acceptance has been sent to the Zamanat team. Thank you!'
          : 'Your feedback has been sent to the Zamanat team. We will review your comments and get back to you shortly.'
      );
      setTimeout(() => setSuccessMessage(null), 8000);
      await fetchData(true);
    } catch { /* */ }
    setSubmittingDecision(false);
  };

  /* ── Styles ── */

  const globalStyles = `
    @keyframes pulseGentle { 0%,100%{opacity:0.3} 50%{opacity:1} }
    @keyframes heroLine { 0%{transform:translateY(-24px);opacity:0} 15%{opacity:0.8} 50%{transform:translateY(24px);opacity:0.8} 85%{opacity:0.8} 100%{transform:translateY(-24px);opacity:0} }
    @keyframes floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
    @keyframes slideInRight { from{transform:translateX(100%)} to{transform:translateX(0)} }
    .text-shimmer-gold {
      color: ${Z.heading};
      -webkit-text-fill-color: ${Z.heading};
    }
    .deck-scroll::-webkit-scrollbar { display: none; }
    .deck-scroll { -ms-overflow-style: none; scrollbar-width: none; }
    p:has(> ul), p:has(> ol) { white-space: normal !important; }
    p > ul, p > ol { margin-top: 2px !important; margin-bottom: 0 !important; }
    ul > li, ol > li { margin-bottom: 1px !important; }
  `;

  /* ════════════════════════════════════════════════════
     EMAIL GATE
     ════════════════════════════════════════════════════ */

  if (authLoading) return null;

  // Hold render for a logged-in user until the pre-gate branding resolves: the
  // org-match bypass depends on proposalOrgId, so rendering earlier would flash
  // the email gate to a legitimately-matching user before their org is known.
  if (!!user && !brandingLoaded) return null;

  if (!emailVerified) {
    return (
      <ProposalThemeContext.Provider value={Z}>
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ background: Z.bg }}>
        <style>{globalStyles}</style>
        <div className="relative z-20 max-w-md w-full">
          <div className="flex justify-center mb-8" style={{ animation: 'floatY 6s ease-in-out infinite' }}>
            <Orb size={70} logoUrl={orgBranding?.secondary_logo_url} hideWhenEmpty />
          </div>

          <Card glow bg={Z.bg} borderColor={Z.gold}>
            <div className="p-8 sm:p-10">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold mb-2" style={{ color: Z.heading }}>Access Proposal</h1>
                <p className="text-sm" style={{ color: Z.gray400 }}>Enter your email to view this proposal.</p>
              </div>

              <form onSubmit={handleVerifyEmail}>
                <label className="block text-[11px] font-bold uppercase tracking-[0.15em] mb-2" style={{ color: Z.gold }}>
                  Email Address
                </label>
                <input
                  type="email" value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                  placeholder="you@company.com" required autoFocus
                  className="w-full rounded-xl px-4 py-3.5 text-sm focus:outline-none transition-all"
                  style={{
                    background: Z.bgCardHover,
                    border: `1px solid ${Z.borderSubtle}`,
                    color: Z.heading,
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = Z.gold; e.target.style.boxShadow = `0 0 0 3px rgba(91,140,82,0.12), inset 0 1px 2px rgba(0,0,0,0.05)`; }}
                  onBlur={(e) => { e.target.style.borderColor = Z.borderSubtle; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.05)'; }}
                />
                {emailError && <p className="text-sm mt-2" style={{ color: Z.coral }}>{emailError}</p>}
                <button
                  type="submit" disabled={verifying || !emailInput.trim()}
                  className="mt-5 w-full py-3.5 text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-40 transition-all"
                  style={{
                    background: Z.gold,
                    color: '#FFFFFF',
                    border: `1px solid ${Z.goldLight}`,
                    animation: 'none',
                  }}
                >
                  {verifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                      Verifying...
                    </span>
                  ) : 'Continue'}
                </button>
              </form>
              <p className="text-[11px] text-center mt-6" style={{ color: Z.gray400 }}>
                Use the email address associated with this proposal.
              </p>
            </div>
          </Card>
        </div>
      </div>
      </ProposalThemeContext.Provider>
    );
  }

  /* ════════════════════════════════════════════════════
     LOADING
     ════════════════════════════════════════════════════ */

  if (loading) {
    return (
      <ProposalThemeContext.Provider value={Z}>
      <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden" style={{ background: Z.bg }}>
        <style>{globalStyles}</style>
        <div className="relative z-10" style={{ animation: 'floatY 3s ease-in-out infinite' }}>
          <Orb size={60} logoUrl={orgBranding?.secondary_logo_url} />
        </div>
        <p className="mt-6 text-xs uppercase tracking-[0.2em] relative z-10"
          style={{ color: `rgba(${Z.headingRgb},0.4)`, animation: 'pulseGentle 2s ease-in-out infinite' }}>
          Loading proposal
        </p>
      </div>
      </ProposalThemeContext.Provider>
    );
  }

  /* ════════════════════════════════════════════════════
     ERROR
     ════════════════════════════════════════════════════ */

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ background: Z.bg }}>
        <style>{globalStyles}</style>
        <div className="relative z-10 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.08)', border: `1px solid rgba(239,68,68,0.15)` }}>
            <svg className="w-8 h-8" style={{ color: Z.coral }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: Z.heading }}>Unable to Load Proposal</h2>
          <p style={{ color: Z.gray400 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  /* ════════════════════════════════════════════════════
     DATA
     ════════════════════════════════════════════════════ */

  const { proposal, client_comments, is_decided, client_decision, client_decision_comment, company_name, client_name } = data;
  const isInactive = data && data.is_active === false;
  const sections = proposal.sections || [];

  const commentsBySection = {};
  (client_comments || []).forEach((c) => {
    if (!commentsBySection[c.section_number]) commentsBySection[c.section_number] = [];
    commentsBySection[c.section_number].push(c);
  });

  const slides = [];
  // Slide 1: cover page (first section)
  if (sections.length > 0) {
    slides.push({ type: 'section', section: sections[0], idx: 0, sectionNumber: sections[0].section_number || 1 });
  }
  // Slide 2: hero (section 01 overview with cover preview)
  slides.push({ type: 'hero', section: sections[0] || null });
  // Remaining sections
  sections.slice(1).forEach((section, idx) => {
    slides.push({ type: 'section', section, idx: idx + 1, sectionNumber: section.section_number || idx + 2 });
  });
  slides.push({ type: 'decision' });
  const totalSlides = slides.length;
  const progress = totalSlides > 1 ? currentSlide / (totalSlides - 1) : 0;

  const drawerComments = commentDrawerSection ? (commentsBySection[commentDrawerSection] || []) : [];
  const drawerSectionTitle = commentDrawerSection
    ? (sections.find((s) => (s.section_number || sections.indexOf(s) + 1) === commentDrawerSection)?.title || `Section ${commentDrawerSection}`)
    : '';

  /* ════════════════════════════════════════════════════
     DECK
     ════════════════════════════════════════════════════ */

  return (
    <ProposalThemeContext.Provider value={Z}>
    <div className="h-screen w-screen overflow-hidden relative select-none" style={{ background: Z.bg }}>
      <style>{globalStyles}</style>

      {/* Global background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0" />
      </div>

      {/* Progress bar — gold gradient on left edge */}
      <div className="fixed top-0 left-0 bottom-0 w-[3px] z-40" style={{ background: 'rgba(0,0,0,0.06)' }}>
        <div className="w-full rounded-b transition-all duration-700 ease-out"
          style={{
            height: `${progress * 100}%`,
            background: `linear-gradient(180deg, ${Z.gold}, ${Z.goldDark})`,
            boxShadow: `0 0 8px rgba(${Z.headingRgb},0.4)`,
          }}
        />
      </div>


      {/* Nav arrows */}
      <button
        onClick={() => goToSlide(currentSlide - 1, totalSlides)}
        disabled={currentSlide === 0 || presenterActive}
        className="fixed left-2 md:left-6 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center bg-white/80 backdrop-blur-xl disabled:opacity-0 disabled:pointer-events-none transition-all duration-500 hover:scale-105"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)', color: Z.heading }}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={() => goToSlide(currentSlide + 1, totalSlides)}
        disabled={currentSlide === totalSlides - 1 || presenterActive}
        className="fixed right-2 md:right-6 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center bg-white/80 backdrop-blur-xl disabled:opacity-0 disabled:pointer-events-none transition-all duration-500 hover:scale-105"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)', color: Z.heading }}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>


      <KeyboardNav currentSlide={currentSlide} totalSlides={totalSlides} goToSlide={goToSlide} />

      {/* Inactive proposal banner — fixed at top */}
      {isInactive && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <InactiveProposalBanner activeVersionUrl={data.active_version_url} />
        </div>
      )}

      {/* ═══ SLIDE CONTAINER ═══ */}
      <div ref={containerRef} className="deck-scroll h-full w-full overflow-x-scroll overflow-y-hidden relative z-10"
        style={{ scrollSnapType: 'x mandatory' }}>
        <div className="flex h-full" style={{ width: `${totalSlides * 100}vw` }}>

          {slides.map((slide, slideIdx) => {
            const isActive = currentSlide === slideIdx;

            /* ── HERO ── */
            if (slide.type === 'hero') {
              const heroSection = slide.section;
              const heroMetrics = heroSection?.key_metrics || [];
              const hasHeroMetrics = heroMetrics.length > 0;

              return (
                <section key="hero" className="w-screen min-w-[100vw] h-screen flex-shrink-0 relative flex items-center overflow-hidden group"
                  style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
                  <SlideBg />
                  <div className="absolute top-12 left-12 md:left-16 lg:left-24 2xl:left-32 z-30" style={entrance(isActive, 0.05, 'down')}>
                    <img src={data?.org_branding?.primary_logo_url || '/images/Zamanat_Logo.png'} alt="Zamanat" className="h-8 w-auto" />
                  </div>

                  {company_name && (
                    <div className="absolute top-12 md:top-7 left-0 right-0 z-20 text-center pointer-events-none" style={entrance(isActive, 0.1, 'down')}>
                      <div className="py-2 md:py-1.5"
                        style={{ background: `linear-gradient(to bottom, ${Z.bg} 0%, ${Z.bg} 60%, transparent 100%)` }}>
                        <span className="text-[11px] md:text-sm font-bold uppercase tracking-[0.3em] text-shimmer-gold">{company_name}</span>
                      </div>
                    </div>
                  )}

                  {/* Top-right page counter */}
                  <div className="absolute top-12 right-20 z-20 flex items-baseline gap-1" style={entrance(isActive, 0.1, 'down')}>
                    <span className="text-3xl md:text-4xl font-medium italic" style={{ color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{String(slideIdx + 1).padStart(2, '0')}</span>
                    <span className="text-sm font-normal" style={{ color: Z.gray400 }}>/ {totalSlides}</span>
                  </div>

                  <div className="relative z-10 w-full pl-20 pr-16 md:pl-24 md:pr-20 lg:pl-32 lg:pr-28 2xl:pl-44 2xl:pr-36 h-full flex flex-col pt-36 pb-20 md:pt-40 md:pb-24 overflow-y-auto deck-scroll select-text">
                    <div>
                      <div className={`grid grid-cols-1 gap-6 lg:gap-12 items-start ${hasHeroMetrics ? 'lg:grid-cols-2' : ''}`}>
                        {/* Left: cover page content */}
                        <div style={entrance(isActive, 0.18, 'left')}>
                          <p className="text-[10px] font-semibold tracking-[1.6px] uppercase mb-5"
                            style={{ color: Z.gold }}>
                            Confidential
                          </p>
                          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium leading-[0.95] tracking-tight mb-5"
                            style={{ color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif', letterSpacing: '-0.03em' }}>
                            <AccentHeading text={heroSection?.title || proposal.title || 'Cover Page'} />
                          </h2>
                          {heroSection?.content && (
                            <div className="text-sm md:text-base lg:text-lg font-light leading-relaxed max-w-lg" style={{ color: Z.gray400 }}>
                              <Md>{typeof heroSection.content === 'string' ? heroSection.content : JSON.stringify(heroSection.content)}</Md>
                            </div>
                          )}
                        </div>

                        {/* Right: key metrics (same style as other sections) */}
                        {hasHeroMetrics && (
                          <div className="flex flex-col justify-start">
                            {heroMetrics.length === 4 ? (
                              <div className="grid gap-3 md:gap-4 grid-cols-2">
                                {heroMetrics.map((m, mi) => (
                                  <div key={mi}
                                    className="flex flex-col justify-between p-5 md:p-6 rounded-[14px] min-h-[140px] md:min-h-[160px]"
                                    style={{ ...entrance(isActive, 0.2 + mi * 0.08, 'right'), background: '#e6edf1' }}>
                                    <p className={`font-medium leading-tight${isMetricNumeric(m.value) ? ' italic' : ''} ${isPlaceholder(m.value) ? 'text-sm md:text-base lg:text-lg' : 'text-2xl md:text-3xl lg:text-4xl'}`}
                                      style={{ color: isMetricNumeric(m.value) ? Z.gold : Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{m.value}</p>
                                    <p className="text-[10px] md:text-[11px] font-semibold tracking-[1.2px] uppercase" style={{ color: Z.heading }}>{m.label}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col gap-3 md:gap-4">
                                {heroMetrics.map((m, mi) => (
                                  <div key={mi}
                                    className="flex items-center justify-between px-5 md:px-6 py-4 md:py-5 rounded-[14px]"
                                    style={{ ...entrance(isActive, 0.2 + mi * 0.08, 'right'), background: '#e6edf1' }}>
                                    <p className="text-[11px] font-semibold tracking-[1.2px] uppercase shrink-0 mr-4" style={{ color: Z.heading }}>{m.label}</p>
                                    <p className={`text-lg md:text-xl font-normal text-right${isMetricNumeric(m.value) ? ' italic' : ''}`}
                                      style={{ color: isMetricNumeric(m.value) ? Z.gold : Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{m.value}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              );
            }

            /* ── SECTION ── */
            if (slide.type === 'section') {
              const { section, sectionNumber } = slide;
              const sectionCommentCount = (commentsBySection[sectionNumber] || []).length;
              const displayNumber = sectionNumber;
              const hasMetrics = section.key_metrics && section.key_metrics.length > 0;
              const hasSubsections = section.subsections && section.subsections.length > 0;
              const isCoverPage = slide.idx === 0;

              /* ── COVER PAGE (first section) ── */
              if (isCoverPage) {
                return (
                  <section key={`section-${sectionNumber}`}
                    className="w-screen min-w-[100vw] h-screen flex-shrink-0 relative flex items-center overflow-hidden"
                    style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always', background: Z.bg }}>

                    <div className="absolute top-12 left-12 md:left-16 lg:left-24 2xl:left-32 z-30" style={entrance(isActive, 0.05, 'down')}>
                      <img src={data?.org_branding?.primary_logo_url || '/images/Zamanat_Logo.png'} alt="Zamanat" className="h-8 w-auto" />
                    </div>

                    {/* Top-right page counter (Figma style) */}
                    <div className="absolute top-12 right-20 z-20 flex items-baseline gap-1" style={entrance(isActive, 0.1, 'down')}>
                      <span className="text-3xl md:text-4xl font-medium italic" style={{ color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{String(slideIdx + 1).padStart(2, '0')}</span>
                      <span className="text-sm font-normal" style={{ color: Z.gray600 }}>/ {totalSlides}</span>
                    </div>

                    <div className="relative z-10 w-full pl-20 pr-12 md:pl-24 md:pr-16 lg:pl-32 lg:pr-24 2xl:pl-44 2xl:pr-32 h-full flex flex-col pt-20 pb-10 md:pt-24 md:pb-12 select-text">
                      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-center">
                        {/* Left: label + title + description */}
                        <div className="flex flex-col justify-center">
                          <p className="text-[11px] font-semibold tracking-[1.4px] uppercase mb-6"
                            style={{ ...entrance(isActive, 0.1, 'left'), color: Z.gold }}>
                            Confidential &middot; Investment Proposal
                          </p>
                          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-medium leading-[0.95] tracking-tight mb-6"
                            style={{ ...entrance(isActive, 0.18, 'left'), color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif', letterSpacing: '-0.03em' }}>
                            <AccentHeading text={proposal.title || 'Investment Proposal'} />
                          </h2>
                          {section.content && (
                            <div className="text-sm md:text-base lg:text-lg font-light leading-relaxed max-w-lg"
                              style={{ ...entrance(isActive, 0.28, 'left'), color: Z.gray400, letterSpacing: '0.01em' }}>
                              {typeof section.content === 'string'
                                ? <Md>{section.content}</Md>
                                : JSON.stringify(section.content, null, 2)}
                            </div>
                          )}
                        </div>

                        {/* Right: 3D globe with concentric rings */}
                        <div className="flex items-center justify-center" style={entrance(isActive, 0.2, 'scale')}>
                          <div className="relative flex items-center justify-center">
                            <div className="absolute w-[320px] h-[320px] md:w-[400px] md:h-[400px] lg:w-[480px] lg:h-[480px] rounded-full border" style={{ borderColor: `rgba(${Z.headingRgb},0.07)` }} />
                            <div className="absolute w-[400px] h-[400px] md:w-[500px] md:h-[500px] lg:w-[580px] lg:h-[580px] rounded-full border" style={{ borderColor: `rgba(${Z.headingRgb},0.04)` }} />
                            <img src={data?.org_branding?.secondary_logo_url || '/images/3d-globe-logo.png'} alt="" className="relative w-[240px] md:w-[320px] lg:w-[380px] h-auto" />
                          </div>
                        </div>
                      </div>

                      {/* Bottom metadata footer */}
                      <div className="flex-shrink-0 mt-auto mb-10 md:mb-14" style={entrance(isActive, 0.4, 'up')}>
                        <div className="pt-4 md:pt-5">
                          <div className="grid grid-cols-3 gap-8 max-w-xl">
                            <div>
                              <p className="text-[10px] font-semibold tracking-[1.2px] uppercase mb-1" style={{ color: Z.gray400 }}>Prepared For</p>
                              <p className="text-sm font-normal" style={{ color: Z.gray400 }}>{company_name}{client_name ? ` — ${client_name}` : ''}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold tracking-[1.2px] uppercase mb-1" style={{ color: Z.gray400 }}>Date</p>
                              <p className="text-sm font-normal" style={{ color: Z.gray400 }}>{new Date(proposal.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold tracking-[1.2px] uppercase mb-1" style={{ color: Z.gray400 }}>Version</p>
                              <p className="text-sm font-normal" style={{ color: Z.gray400 }}>v{proposal.version}.0</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              return (
                <section key={`section-${sectionNumber}`}
                  className="w-screen min-w-[100vw] h-screen flex-shrink-0 relative flex items-center overflow-hidden group"
                  style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
                  <SlideBg />
                  <div className="absolute top-12 left-12 md:left-16 lg:left-24 2xl:left-32 z-30" style={entrance(isActive, 0.05, 'down')}>
                    <img src={data?.org_branding?.primary_logo_url || '/images/Zamanat_Logo.png'} alt="Zamanat" className="h-8 w-auto" />
                  </div>
                  {company_name && (
                    <div className="absolute top-12 md:top-7 left-0 right-0 z-20 text-center pointer-events-none" style={entrance(isActive, 0.1, 'down')}>
                      <div className="py-2 md:py-1.5"
                        style={{ background: `linear-gradient(to bottom, ${Z.bg} 0%, ${Z.bg} 60%, transparent 100%)` }}>
                        <span className="text-[11px] md:text-sm font-bold uppercase tracking-[0.3em] text-shimmer-gold">{company_name}</span>
                      </div>
                    </div>
                  )}

                  {/* Top-right page counter (Figma style) */}
                  <div className="absolute top-12 right-20 z-20 flex items-baseline gap-1" style={entrance(isActive, 0.1, 'down')}>
                    <span className="text-3xl md:text-4xl font-medium italic" style={{ color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{String(slideIdx + 1).padStart(2, '0')}</span>
                    <span className="text-sm font-normal" style={{ color: Z.gray400 }}>/ {totalSlides}</span>
                  </div>

                  <div className="relative z-10 w-full pl-20 pr-16 md:pl-24 md:pr-20 lg:pl-32 lg:pr-28 2xl:pl-44 2xl:pr-36 h-full flex flex-col pt-36 pb-20 md:pt-40 md:pb-24 overflow-y-auto deck-scroll select-text">
                    <div className="flex flex-col flex-1">

                    {/* ── Two-column: heading left, metrics right ── */}
                    {hasMetrics ? (
                      <>
                        <div className="flex items-center justify-end mb-6 md:mb-8 flex-shrink-0">
                          {!isPortalUser && (!is_decided || sectionCommentCount > 0) && (
                            <div className={sectionCommentCount === 0 && !is_decided ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : ''}>
                              <button onClick={() => openComments(sectionNumber)}
                                className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-white/80 backdrop-blur-sm transition-all duration-500 hover:scale-105"
                                style={{
                                  ...entrance(isActive, 0.15, 'right'),
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)',
                                  color: Z.heading,
                                }}>
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                <span className="text-xs md:text-sm font-medium">{sectionCommentCount > 0 ? sectionCommentCount : (is_decided ? '' : 'Comment')}</span>
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 flex-shrink-0 items-start">
                          {/* Left: heading + brief text */}
                          <div className="flex flex-col justify-start">
                            <h2 className="text-2xl md:text-4xl lg:text-6xl font-medium mb-3 leading-[1.0] tracking-tight"
                              style={{ ...entrance(isActive, 0.15, 'left'), color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif', letterSpacing: '-0.025em' }}>
                              <AccentHeading text={section.title || `Section ${sectionNumber}`} />
                            </h2>
                            <div style={entrance(isActive, 0.22, 'left')} className="mb-3 md:mb-5" />
                            {section.content && (
                              <div className="text-sm md:text-base lg:text-lg font-light leading-relaxed max-w-lg"
                                style={{ ...entrance(isActive, 0.28, 'left'), color: Z.gray400, letterSpacing: '0.01em' }}>
                                {typeof section.content === 'string'
                                  ? <Md>{section.content}</Md>
                                  : JSON.stringify(section.content, null, 2)}
                              </div>
                            )}
                          </div>

                          {/* Right: metrics */}
                          <div className="flex flex-col justify-start">
                            {section.key_metrics.length === 4 ? (
                              /* 4-metric grid — 2×2 boxes */
                              <div className="grid gap-3 md:gap-4 grid-cols-2">
                                {section.key_metrics.map((m, mi) => (
                                  <div key={mi}
                                    className="flex flex-col justify-between p-5 md:p-6 rounded-[14px] min-h-[140px] md:min-h-[160px]"
                                    style={{
                                      ...entrance(isActive, 0.2 + mi * 0.08, 'right'),
                                      background: '#e6edf1',
                                    }}>
                                    <p className={`font-medium leading-tight${isMetricNumeric(m.value) ? ' italic' : ''} ${isPlaceholder(m.value) ? 'text-sm md:text-base lg:text-lg' : 'text-2xl md:text-3xl lg:text-4xl'}`} style={{ color: isMetricNumeric(m.value) ? Z.gold : Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{m.value}</p>
                                    <p className="text-[10px] md:text-[11px] font-semibold tracking-[1.2px] uppercase" style={{ color: Z.heading }}>{m.label}</p>
                                  </div>
                                ))}
                              </div>
                            ) : section.key_metrics.length === 2 ? (
                              /* 2-metric layout — first as full-width card, second as pill */
                              <div className="flex flex-col gap-3 md:gap-4">
                                {/* First metric: full-width card */}
                                {(() => { const m = section.key_metrics[0]; return (
                                  <div
                                    className="flex flex-col justify-between p-5 md:p-6 rounded-[14px] min-h-[120px] md:min-h-[140px]"
                                    style={{ ...entrance(isActive, 0.2, 'right'), background: '#e6edf1' }}>
                                    <p className={`font-medium leading-tight${isMetricNumeric(m.value) ? ' italic' : ''} ${isPlaceholder(m.value) ? 'text-sm md:text-base lg:text-lg' : 'text-4xl md:text-5xl lg:text-6xl'}`} style={{ color: isMetricNumeric(m.value) ? Z.gold : Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{m.value}</p>
                                    <p className="text-[10px] md:text-[11px] font-semibold tracking-[1.2px] uppercase" style={{ color: Z.heading }}>{m.label}</p>
                                  </div>
                                ); })()}
                                {/* Second metric: horizontal pill */}
                                {(() => { const m = section.key_metrics[1]; return (
                                  <div
                                    className="flex items-center justify-between px-5 md:px-6 py-4 md:py-5 rounded-[14px]"
                                    style={{ ...entrance(isActive, 0.28, 'right'), background: '#e6edf1' }}>
                                    <p className="text-[11px] font-semibold tracking-[1.2px] uppercase shrink-0 mr-4" style={{ color: Z.heading }}>{m.label}</p>
                                    <p className={`text-lg md:text-xl font-normal text-right${isMetricNumeric(m.value) ? ' italic' : ''}`} style={{ color: isMetricNumeric(m.value) ? Z.gold : Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{m.value}</p>
                                  </div>
                                ); })()}
                              </div>
                            ) : (
                              /* Horizontal pills — label left, value right */
                              <div className="flex flex-col gap-3 md:gap-4">
                                {section.key_metrics.map((m, mi) => (
                                  <div key={mi}
                                    className="flex items-center justify-between px-5 md:px-6 py-4 md:py-5 rounded-[14px]"
                                    style={{
                                      ...entrance(isActive, 0.2 + mi * 0.08, 'right'),
                                      background: '#e6edf1',
                                    }}>
                                    <p className="text-[11px] font-semibold tracking-[1.2px] uppercase shrink-0 mr-4" style={{ color: Z.heading }}>{m.label}</p>
                                    <p className={`text-lg md:text-xl font-normal text-right${isMetricNumeric(m.value) ? ' italic' : ''}`} style={{ color: isMetricNumeric(m.value) ? Z.gold : Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{m.value}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Subsections below if any */}
                        {hasSubsections && (
                          <SubsectionRenderer subsections={section.subsections} isActive={isActive} baseDelay={0.4} className="mt-12 md:mt-16 flex-shrink-0" />
                        )}
                      </>
                    ) : (
                      /* ── No metrics: full-width content layout ── */
                      <>
                        <div className="flex items-center justify-end mb-6 md:mb-8 flex-shrink-0">
                          {!isPortalUser && (!is_decided || sectionCommentCount > 0) && (
                            <div className={sectionCommentCount === 0 && !is_decided ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : ''}>
                              <button onClick={() => openComments(sectionNumber)}
                                className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-white/80 backdrop-blur-sm transition-all duration-500 hover:scale-105"
                                style={{
                                  ...entrance(isActive, 0.15, 'right'),
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)',
                                  color: Z.heading,
                                }}>
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                <span className="text-xs md:text-sm font-medium">{sectionCommentCount > 0 ? sectionCommentCount : (is_decided ? '' : 'Comment')}</span>
                              </button>
                            </div>
                          )}
                        </div>
                        <h2 className="text-2xl md:text-4xl lg:text-5xl font-medium mb-2 leading-[1.0] tracking-tight flex-shrink-0"
                          style={{ ...entrance(isActive, 0.2, 'left'), color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif', letterSpacing: '-0.025em' }}>
                          <AccentHeading text={section.title || `Section ${sectionNumber}`} />
                        </h2>
                        <div style={entrance(isActive, 0.26, 'left')} className="mb-4 md:mb-6 flex-shrink-0" />

                        {section.content && (
                          <div className="mb-4 md:mb-5 flex-shrink-0" style={entrance(isActive, 0.3, 'left')}>
                            <div className="leading-relaxed text-sm md:text-base lg:text-lg font-light whitespace-pre-line" style={{ color: Z.gray400, letterSpacing: '0.01em' }}>
                              {typeof section.content === 'string' ? <Md>{section.content}</Md> : JSON.stringify(section.content, null, 2)}
                            </div>
                          </div>
                        )}

                        {hasSubsections && (
                          <SubsectionRenderer subsections={section.subsections} isActive={isActive} baseDelay={0.3} className="mt-6 md:mt-8 mb-4 md:mb-5 flex-shrink-0" />
                        )}
                      </>
                    )}
                    </div>
                  </div>
                </section>
              );
            }

            /* ── DECISION ── */
            if (slide.type === 'decision') {
              return (
                <section key="decision" className="w-screen min-w-[100vw] h-screen flex-shrink-0 relative flex items-center justify-center overflow-hidden"
                  style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always', background: '#eef3f6' }}>
                  <div className="absolute top-12 left-12 md:left-16 lg:left-24 2xl:left-32 z-30" style={entrance(isActive, 0.05, 'down')}>
                    <img src={data?.org_branding?.primary_logo_url || '/images/Zamanat_Logo.png'} alt="Zamanat" className="h-8 w-auto" />
                  </div>
                  {/* Top-right page counter (Figma style) */}
                  <div className="absolute top-12 right-20 z-20 flex items-baseline gap-1" style={entrance(isActive, 0.1, 'down')}>
                    <span className="text-3xl md:text-4xl font-medium italic" style={{ color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif' }}>{String(slideIdx + 1).padStart(2, '0')}</span>
                    <span className="text-sm font-normal" style={{ color: Z.gray400 }}>/ {totalSlides}</span>
                  </div>

                  <div className="relative z-10 text-center max-w-2xl mx-auto px-6 select-text">
                    {is_decided ? (
                      <>
                        <div style={entrance(isActive, 0.1, 'scale')}>
                          <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-8"
                            style={{
                              background: client_decision === 'approved' ? 'rgba(5,150,105,0.1)' : 'rgba(217,119,6,0.1)',
                              border: `2px solid ${client_decision === 'approved' ? 'rgba(5,150,105,0.25)' : 'rgba(217,119,6,0.25)'}`,
                              boxShadow: client_decision === 'approved' ? '0 0 40px rgba(5,150,105,0.15)' : '0 0 40px rgba(217,119,6,0.12)',
                            }}>
                            {client_decision === 'approved' ? (
                              <svg className="w-12 h-12" style={{ color: Z.green }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-12 h-12" style={{ color: Z.amber }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-medium mb-4 leading-[1.0] tracking-tight" style={{ ...entrance(isActive, 0.2, 'up'), color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif', letterSpacing: '-0.025em' }}>
                          <AccentHeading text={client_decision === 'approved' ? 'Proposal Accepted' : 'Sent Back for Changes'} />
                        </h2>
                        {client_decision_comment && (
                          <div style={entrance(isActive, 0.35, 'up')}>
                            <Card>
                              <div className="p-5 text-left">
                                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: Z.gray600 }}>Your feedback</p>
                                <p className="text-sm leading-relaxed" style={{ color: Z.gray400 }}>{client_decision_comment}</p>
                              </div>
                            </Card>
                          </div>
                        )}
                        {successMessage && (
                          <div className="mt-6 flex items-center gap-2.5 justify-center" style={entrance(isActive, 0.45, 'up')}>
                            <svg className="w-4 h-4 flex-shrink-0" style={{ color: Z.green }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <p className="text-sm" style={{ color: Z.gray400 }}>{successMessage}</p>
                          </div>
                        )}
                      </>
                    ) : isInactive ? (
                      <>
                        <div className="flex justify-center mb-10" style={{ ...entrance(isActive, 0.05, 'scale'), animation: isActive ? 'floatY 5s ease-in-out infinite' : 'none' }}>
                          <Orb size={80} logoUrl={data?.org_branding?.secondary_logo_url} />
                        </div>
                        <div style={entrance(isActive, 0.15, 'down')}>
                          <span className="inline-block text-sm font-medium tracking-widest uppercase rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm"
                            style={{ color: Z.amber, background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.2)' }}>
                            Outdated Version
                          </span>
                        </div>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium mb-4 leading-[1.0] tracking-tight" style={{ ...entrance(isActive, 0.25, 'up'), color: Z.gray400, fontFamily: '"Libre Caslon Text", Georgia, serif', letterSpacing: '-0.025em' }}>
                          This version is no longer current
                        </h2>
                        <div className="mt-8">
                          <InactiveProposalBanner activeVersionUrl={data.active_version_url} />
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Globe with concentric rings */}
                        <div className="relative flex items-center justify-center mb-6" style={{ ...entrance(isActive, 0.05, 'scale'), animation: isActive ? 'floatY 5s ease-in-out infinite' : 'none' }}>
                          <div className="absolute w-[260px] h-[260px] rounded-full border" style={{ borderColor: `rgba(${Z.headingRgb},0.07)` }} />
                          <div className="absolute w-[360px] h-[360px] rounded-full border" style={{ borderColor: `rgba(${Z.headingRgb},0.04)` }} />
                          <img src={data?.org_branding?.secondary_logo_url || '/images/3d-globe-logo.png'} alt="" className="relative w-36 h-36 object-contain" />
                        </div>

                        <p className="text-[11px] font-semibold tracking-[1.4px] uppercase mb-5" style={{ ...entrance(isActive, 0.15, 'down'), color: Z.gold }}>
                          Your Decision
                        </p>

                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-medium mb-4 leading-[1.0] tracking-tight" style={{ ...entrance(isActive, 0.25, 'up'), color: Z.heading, fontFamily: '"Libre Caslon Text", Georgia, serif', letterSpacing: '-0.025em' }}>
                          <AccentHeading text="Ready to proceed?" />
                        </h2>

                        <p className="mb-10 text-base" style={{ ...entrance(isActive, 0.38, 'up'), color: Z.gray400 }}>
                          {isPortalUser
                            ? 'Review complete. The client can submit their decision from their copy of this proposal.'
                            : 'Review complete. Submit your decision below to move into structuring & due diligence.'}
                        </p>

                        {!isPortalUser && (
                          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center" style={entrance(isActive, 0.45, 'up')}>
                            <button
                              onClick={() => { setShowDecisionDialog('approve'); setDecisionComment(''); }}
                              className="w-full sm:w-auto px-12 py-4 text-sm font-semibold rounded-xl hover:opacity-80 transition-all"
                              style={{ background: '#000000', color: '#FFFFFF' }}>
                              Accept Proposal
                            </button>
                            <button
                              onClick={() => { setShowDecisionDialog('reject'); setDecisionComment(''); }}
                              className="w-full sm:w-auto px-12 py-4 text-sm font-semibold rounded-xl transition-all duration-200 hover:bg-white/50"
                              style={{ color: Z.heading, border: `1.5px solid ${Z.heading}`, background: 'transparent' }}>
                              Send Back
                            </button>
                          </div>
                        )}

                        {data.expires_at && (
                          <p className="text-[11px] mt-10" style={{ ...entrance(isActive, 0.55, 'up'), color: Z.gray400 }}>
                            Expires {new Date(data.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </section>
              );
            }

            return null;
          })}
        </div>
      </div>

      {/* ═══ OVERLAYS ═══ */}

      {/* Comment Drawer */}
      {commentDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setCommentDrawerOpen(false)}>
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm h-full flex flex-col border-l"
            style={{ background: Z.heading, borderColor: Z.borderSubtle, animation: 'slideInRight 0.3s cubic-bezier(0.16,1,0.3,1)', boxShadow: '-4px 0 16px rgba(0,0,0,0.08)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
              <div>
                <h3 className="text-sm font-bold" style={{ color: Z.gray400 }}>Comments</h3>
                <p className="text-xs mt-0.5 truncate max-w-[250px]" style={{ color: Z.gray400 }}>{drawerSectionTitle}</p>
              </div>
              <button onClick={() => setCommentDrawerOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-105"
                style={{ color: Z.gray600, background: Z.gray800 }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3 deck-scroll">
              {drawerComments.length > 0 ? drawerComments.map((c) => (
                <div key={c.id} className="group rounded-2xl p-4 transition-all duration-300"
                  style={{ background: Z.bgCardHover, border: `1px solid ${Z.borderSubtle}` }}>
                  {c.author_name && <p className="text-xs font-bold mb-1" style={{ color: Z.heading }}>{c.author_name}</p>}
                  <p className="text-sm leading-relaxed" style={{ color: Z.gray400 }}>{c.comment}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px]" style={{ color: Z.gray600 }}>
                      {c.created_at && new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                    {!is_decided && (
                      <button onClick={() => handleDeleteComment(c.id)} disabled={deletingComment === c.id}
                        className="opacity-0 group-hover:opacity-100 transition-all" style={{ color: Z.coral }}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )) : (
                <p className="text-sm text-center py-10" style={{ color: Z.gray400 }}>
                  {is_decided ? 'No comments on this section.' : 'No comments yet. Add one below.'}
                </p>
              )}
            </div>

            {!isPortalUser && !is_decided && !isInactive && commentDrawerSection && (
              <div className="border-t p-5" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                <textarea
                  value={commentInputs[commentDrawerSection] || ''}
                  onChange={(e) => setCommentInputs((p) => ({ ...p, [commentDrawerSection]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(commentDrawerSection); } }}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full rounded-xl px-4 py-3 text-sm resize-none focus:outline-none transition-all"
                  style={{ background: Z.bgCardHover, border: `1px solid ${Z.borderSubtle}`, color: Z.heading }}
                  onFocus={(e) => { e.target.style.borderColor = Z.heading; e.target.style.boxShadow = `0 0 0 3px rgba(${Z.headingRgb},0.12)`; }}
                  onBlur={(e) => { e.target.style.borderColor = Z.borderSubtle; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  onClick={() => handleAddComment(commentDrawerSection)}
                  disabled={!(commentInputs[commentDrawerSection] || '').trim() || addingComment === commentDrawerSection}
                  className="mt-3 w-full py-2.5 text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-30 transition-all"
                  style={{ background: Z.gold, color: '#FFFFFF', border: `1px solid ${Z.goldLight}` }}>
                  {addingComment === commentDrawerSection ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LiveKit Avatar Presenter Panel */}
      {presenterActive && (
        <LiveKitAvatarPanel
          isActive={presenterActive}
          hook={livekit}
          mode="interactive"
          onClose={() => { livekit.disconnect(); setPresenterActive(false); }}
          onSlideAdvance={(section) => goToSlide(section, totalSlides, false, true)}
          avatarName={livekit.avatarName || data?.avatar_name}
          avatarImageUrl={data?.avatar_image_url}
        />
      )}

      {/* Floating AI button with expandable menu */}
      {emailVerified && data && !chatOpen && !presenterActive && !isInactive && (
        <div className="fixed bottom-6 right-6 z-40">
          {/* Expanded menu — shows above the button */}
          {aiMenuOpen && (
            <>
              <div className="fixed inset-0" onClick={() => setAiMenuOpen(false)} />
              <div className="absolute bottom-16 right-0 flex flex-col items-end gap-3 mb-2">
                {/* Ask AI — circular icon button */}
                <button
                  onClick={() => { setAiMenuOpen(false); setChatOpen(true); }}
                  className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(24px)',
                    border: `1px solid ${Z.border}`,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                  }}
                  title="Ask AI"
                >
                  <svg className="w-6 h-6" style={{ color: Z.heading }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                </button>

                {/* Present — avatar image button */}
                {data.livekit_enabled === true && (
                  <button
                    onClick={() => { setAiMenuOpen(false); setPresenterActive(true); livekit.connect(token); }}
                    className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110 overflow-hidden"
                    style={{
                      background: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(24px)',
                      border: `1px solid ${Z.heading}`,
                      boxShadow: `0 4px 20px rgba(${Z.headingRgb},0.2)`,
                    }}
                    title="Present Proposal"
                  >
                    {data.avatar_image_url ? (
                      <img src={data.avatar_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-6 h-6" style={{ color: Z.heading }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </>
          )}

          {/* AI icon button — dims when menu is open */}
          <button
            onClick={() => setAiMenuOpen(!aiMenuOpen)}
            className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{
              background: aiMenuOpen
                ? 'rgba(100,100,100,0.5)'
                : `linear-gradient(135deg, ${Z.heading}, rgba(${Z.headingRgb},0.85))`,
              boxShadow: aiMenuOpen
                ? '0 2px 10px rgba(0,0,0,0.2)'
                : `0 4px 20px rgba(${Z.headingRgb},0.2)`,
              opacity: aiMenuOpen ? 0.6 : 1,
              transition: 'all 0.2s ease',
            }}
            title="AI Assistant"
          >
            <svg className="w-6 h-6" fill="none" stroke={aiMenuOpen ? '#fff' : '#FFFFFF'} strokeWidth={2} viewBox="0 0 24 24">
              {aiMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              )}
            </svg>
          </button>
        </div>
      )}

      {/* Ask AI chat panel */}
      {emailVerified && data && !isInactive && (
        <ProposalChatPanel
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          token={token}
        />
      )}

      {/* Decision Dialog */}
      {showDecisionDialog && !isInactive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setShowDecisionDialog(null)}>
          <div className="absolute inset-0 bg-black/20 backdrop-blur-md" />
          <div className="relative rounded-[26px] max-w-md w-full p-6 overflow-hidden"
            style={{ background: Z.bgCard, border: `1px solid ${Z.borderSubtle}`, animation: 'menuFadeIn 0.2s ease-out', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Gold top accent */}
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${Z.gold}, transparent)` }} />

            <h3 className="text-xl font-bold mb-1" style={{ color: Z.heading }}>
              {showDecisionDialog === 'approve' ? 'Accept Proposal' : 'Send Back for Changes'}
            </h3>
            <p className="text-sm mb-5" style={{ color: Z.gray400 }}>
              {showDecisionDialog === 'approve'
                ? 'Please provide any final comments or feedback along with your approval.'
                : 'Please describe the changes you\'d like us to make.'}
            </p>
            <textarea
              value={decisionComment}
              onChange={(e) => setDecisionComment(e.target.value)}
              rows={4}
              placeholder="Your feedback (minimum 20 characters)..."
              className="w-full rounded-xl px-4 py-3 text-sm resize-none focus:outline-none transition-all"
              style={{ background: Z.bgCardHover, border: `1px solid ${Z.borderSubtle}`, color: Z.heading }}
              onFocus={(e) => { e.target.style.borderColor = Z.gold; e.target.style.boxShadow = `0 0 0 3px rgba(${Z.headingRgb},0.12)`; }}
              onBlur={(e) => { e.target.style.borderColor = Z.borderSubtle; e.target.style.boxShadow = 'none'; }}
            />
            <p className="text-xs mt-1.5 mb-5" style={{ color: Z.gray600 }}>
              {decisionComment.trim().length}/20 characters minimum
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowDecisionDialog(null)}
                className="flex-1 py-3 text-sm font-medium rounded-xl transition-colors"
                style={{ color: Z.gray400, background: Z.gray800, border: `1px solid ${Z.borderSubtle}` }}>
                Cancel
              </button>
              <button onClick={() => handleDecision(showDecisionDialog)}
                disabled={decisionComment.trim().length < 20 || submittingDecision}
                className="flex-1 py-3 text-sm font-semibold rounded-xl transition-all disabled:opacity-30"
                style={{
                  background: showDecisionDialog === 'approve' ? Z.gold : Z.amber,
                  color: '#FFFFFF',
                  border: showDecisionDialog === 'approve' ? `1px solid ${Z.goldLight}` : '1px solid transparent',
                }}>
                {submittingDecision ? 'Submitting...' : showDecisionDialog === 'approve' ? 'Confirm Acceptance' : 'Confirm Send Back'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ProposalThemeContext.Provider>
  );
};

/* ── Keyboard Nav ── */

const KeyboardNav = ({ currentSlide, totalSlides, goToSlide }) => {
  useEffect(() => {
    const h = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); goToSlide(currentSlide + 1, totalSlides); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); goToSlide(currentSlide - 1, totalSlides); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [currentSlide, totalSlides, goToSlide]);
  return null;
};

export default ClientProposalReviewPageV4;
